import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Calendar as CalendarIcon,
  Search,
  Download,
  Eye,
  Filter,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  ClientEventReportDTO,
  ClientEventReportFilter,
  EventStatusEnum,
} from "@/types/reports";
import { reportService } from "@/services/reportService";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { BackLink } from "@/components/brand/BackLink";
import {
  ResponsiveTable,
  ResponsiveTableBody,
  ResponsiveTableCell,
  ResponsiveTableHead,
  ResponsiveTableHeader,
  ResponsiveTableRow,
} from "@/components/ui/responsive-table";

const getStatusBadgeVariant = (status: EventStatusEnum) => {
  switch (status) {
    case EventStatusEnum.NEW:
      return "default";
    case EventStatusEnum.AWAITINGPAYMENT:
      return "outline";
    case EventStatusEnum.COMPLETE:
      return "default";
    case EventStatusEnum.CANCELLED:
      return "destructive";
    default:
      return "secondary";
  }
};

const formatEventDate = (eventDate: string | null | undefined) =>
  eventDate ? format(new Date(eventDate + "T00:00:00"), "PP") : "No date";

const formatTimestamp = (timestamp: string | null | undefined) =>
  timestamp ? format(new Date(timestamp), "PPp") : "Unknown";

function Detail({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="font-medium">{label}</dt>
      <dd className="text-muted-foreground">{children || "None"}</dd>
    </div>
  );
}

export default function ClientEventReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reportData, setReportData] = useState<ClientEventReportDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ClientEventReportFilter>({});
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const { toast } = useToast();

  const events = (reportData || []).filter((event) => event != null);

  const toggleExpanded = (key: string) =>
    setExpandedIds((current) =>
      current.includes(key)
        ? current.filter((id) => id !== key)
        : [...current, key],
    );

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await reportService.fetchClientEventReport({});
        setReportData(data);
      } catch (error) {
        console.error("Failed to fetch initial report data:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load report data";
        setError(errorMessage);
        toast({
          title: "Error Loading Report",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [toast]);

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportService.fetchClientEventReport(filters);
      setReportData(data);
    } catch (error) {
      console.error("Failed to fetch report data:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch report data";
      setError(errorMessage);
      toast({
        title: "Error Fetching Report",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = <K extends keyof ClientEventReportFilter>(
    key: K,
    value: ClientEventReportFilter[K],
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilters = () => {
    fetchReportData();
  };

  const clearFilters = async () => {
    setFilters({});
    // Reload data with no filters
    setLoading(true);
    setError(null);
    try {
      const data = await reportService.fetchClientEventReport({});
      setReportData(data);
    } catch (error) {
      console.error("Failed to reload data:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to reload data";
      setError(errorMessage);
      toast({
        title: "Error Reloading Report",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const exportData = async () => {
    try {
      await reportService.exportClientEventReport(filters);
      toast({
        title: "Export Successful",
        description: "Report has been exported successfully.",
        variant: "default",
      });
    } catch (error) {
      console.error("Export failed:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to export report";
      toast({
        title: "Export Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Back Navigation */}
      <div className="flex items-center gap-4">
        <BackLink to="/admin/reports">Back to Reports</BackLink>
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Client Event Report
          </h1>
          <p className="text-muted-foreground">
            Comprehensive overview of client events with detailed information
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
          </Button>
          <Button onClick={exportData} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
            <CardDescription>
              Filter the report data based on your criteria
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Event Status Filter */}
              <div className="space-y-2">
                <Label htmlFor="status-filter">Event Status</Label>
                <Select
                  onValueChange={(value) =>
                    handleFilterChange(
                      "eventStatuses",
                      value === "ALL" ? undefined : [value as EventStatusEnum],
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Statuses</SelectItem>
                    {Object.values(EventStatusEnum).map((status) => (
                      <SelectItem key={status} value={status}>
                        {status?.replace("_", " ") || status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range */}
              <div className="space-y-2">
                <Label htmlFor="date-from">Event Date From</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.eventDateFrom
                        ? format(
                            new Date(filters.eventDateFrom + "T00:00:00"),
                            "PPP",
                          )
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={
                        filters.eventDateFrom
                          ? new Date(filters.eventDateFrom + "T00:00:00")
                          : undefined
                      }
                      onSelect={(date) =>
                        handleFilterChange(
                          "eventDateFrom",
                          date ? format(date, "yyyy-MM-dd") : "",
                        )
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date-to">Event Date To</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.eventDateTo
                        ? format(
                            new Date(filters.eventDateTo + "T00:00:00"),
                            "PPP",
                          )
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={
                        filters.eventDateTo
                          ? new Date(filters.eventDateTo + "T00:00:00")
                          : undefined
                      }
                      onSelect={(date) =>
                        handleFilterChange(
                          "eventDateTo",
                          date ? format(date, "yyyy-MM-dd") : "",
                        )
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Search Fields */}
              <div className="space-y-2">
                <Label htmlFor="client-search">Client Name</Label>
                <Input
                  placeholder="Search by client name..."
                  value={filters.clientNameSearch || ""}
                  onChange={(e) =>
                    handleFilterChange("clientNameSearch", e.target.value)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company-search">Company Name</Label>
                <Input
                  placeholder="Search by company..."
                  value={filters.companySearch || ""}
                  onChange={(e) =>
                    handleFilterChange("companySearch", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <Button
                onClick={applyFilters}
                className="flex items-center gap-2"
              >
                <Search className="h-4 w-4" />
                Apply Filters
              </Button>
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Report Results</CardTitle>
              <CardDescription>
                Found {events.length} events matching your criteria
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
              <h3 className="text-lg font-medium text-destructive mb-2">
                Error Loading Report
              </h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => fetchReportData()} variant="outline">
                Try Again
              </Button>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-muted-foreground">
                  Loading report data...
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {events.length > 0 && (
                <div className="rounded-md border">
                  <ResponsiveTable>
                    <ResponsiveTableHeader>
                      <ResponsiveTableRow>
                        <ResponsiveTableHead>Event Date</ResponsiveTableHead>
                        <ResponsiveTableHead>Client</ResponsiveTableHead>
                        <ResponsiveTableHead>Contact</ResponsiveTableHead>
                        <ResponsiveTableHead>Location</ResponsiveTableHead>
                        <ResponsiveTableHead className="text-right">
                          Tables
                        </ResponsiveTableHead>
                        <ResponsiveTableHead className="text-right">
                          Price
                        </ResponsiveTableHead>
                        <ResponsiveTableHead>Status</ResponsiveTableHead>
                        <ResponsiveTableHead className="w-10">
                          <span className="sr-only">Details</span>
                        </ResponsiveTableHead>
                      </ResponsiveTableRow>
                    </ResponsiveTableHeader>
                    <ResponsiveTableBody>
                      {events.map((event, index) => {
                        const key = event.eventId || `event-${index}`;
                        const isExpanded = expandedIds.includes(key);
                        return (
                          <React.Fragment key={key}>
                            <ResponsiveTableRow
                              mobileTitle={
                                event.companyName || event.clientName || "Event"
                              }
                            >
                              <ResponsiveTableCell
                                mobileLabel="Event Date"
                                mobilePriority="high"
                              >
                                {formatEventDate(event.eventDate)}
                              </ResponsiveTableCell>
                              <ResponsiveTableCell
                                mobileLabel="Client"
                                mobilePriority="high"
                              >
                                <div className="font-medium">
                                  {event.clientName || "N/A"}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {event.companyName || "No company"}
                                </div>
                              </ResponsiveTableCell>
                              <ResponsiveTableCell
                                mobileLabel="Contact"
                                mobilePriority="medium"
                              >
                                <div>{event.contactEmail || "N/A"}</div>
                                <div className="text-xs text-muted-foreground">
                                  {event.contactPhone || "No phone"}
                                </div>
                              </ResponsiveTableCell>
                              <ResponsiveTableCell
                                mobileLabel="Location"
                                mobilePriority="medium"
                              >
                                <div>{event.location || "N/A"}</div>
                                <div className="text-xs text-muted-foreground">
                                  {event.address || "No address"}
                                </div>
                              </ResponsiveTableCell>
                              <ResponsiveTableCell
                                mobileLabel="Tables"
                                mobilePriority="medium"
                                className="text-right tabular-nums"
                              >
                                {event.totalTables || 0}
                              </ResponsiveTableCell>
                              <ResponsiveTableCell
                                mobileLabel="Price"
                                mobilePriority="high"
                                className="text-right tabular-nums"
                              >
                                ${(event.totalPrice || 0).toLocaleString()}
                              </ResponsiveTableCell>
                              <ResponsiveTableCell
                                mobileLabel="Status"
                                mobilePriority="high"
                              >
                                <Badge
                                  variant={getStatusBadgeVariant(
                                    event.eventStatus,
                                  )}
                                >
                                  {event.eventStatus?.replace("_", " ") ||
                                    "Unknown"}
                                </Badge>
                              </ResponsiveTableCell>
                              <ResponsiveTableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  aria-expanded={isExpanded}
                                  onClick={() => toggleExpanded(key)}
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                  <span className="sr-only">
                                    {`Details for ${
                                      event.companyName ||
                                      event.clientName ||
                                      key
                                    }`}
                                  </span>
                                </Button>
                              </ResponsiveTableCell>
                            </ResponsiveTableRow>

                            {isExpanded && (
                              <ResponsiveTableRow className="bg-muted/30">
                                <ResponsiveTableCell colSpan={8}>
                                  <dl className="grid gap-3 py-2 text-sm sm:grid-cols-2">
                                    <Detail label="Notes">
                                      {event.additionalInfo}
                                    </Detail>
                                    <Detail label="Delivery">
                                      {event.deliveryInfo}
                                    </Detail>
                                    <Detail label="Dealer instructions">
                                      {event.dealerSpecialInstructions}
                                    </Detail>
                                    <Detail label="Created">
                                      {formatTimestamp(event.createdDate)}
                                    </Detail>
                                    <Detail label="Last modified">
                                      {formatTimestamp(event.lastModifiedDate)}
                                    </Detail>
                                  </dl>
                                </ResponsiveTableCell>
                              </ResponsiveTableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </ResponsiveTableBody>
                  </ResponsiveTable>
                </div>
              )}

              {events.length === 0 && !loading && (
                <div className="text-center py-8">
                  <Eye className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No events found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your filter criteria
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
